
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getTeamSizeGuidelines } from "../utils/teamSizeUtils";

export const useProjectAnalysis = (selectedProject: any) => {
  const [requirements, setRequirements] = useState("");
  const [teamSize, setTeamSize] = useState("medium");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recommendation, setRecommendation] = useState<any | null>(null);
  const [resources, setResources] = useState<any[]>([]);
  const [activeAnalysis, setActiveAnalysis] = useState("overview");
  const [apiError, setApiError] = useState<string | null>(null);
  const [savedToDatabase, setSavedToDatabase] = useState(false);
  const [showNetworkAlert, setShowNetworkAlert] = useState(false);

  useEffect(() => {
    fetchResources();
  }, [selectedProject]);

  const fetchResources = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      let query = supabase.from('resources').select('*').eq('user_id', user.id);
      
      if (selectedProject) {
        query = query.eq('project_id', selectedProject.id);
      }
      
      const { data, error } = await query.order('resource_id', { ascending: true });
      
      if (error) {
        console.error('Error fetching resources:', error);
        return;
      }
      setResources(data || []);
    } catch (error) {
      console.error('Error in fetchResources:', error);
    }
  };

  const analyzeProject = async () => {
    if (!selectedProject || !requirements.trim()) {
      toast.error("Please select a project and provide detailed requirements");
      return;
    }
    setIsAnalyzing(true);
    setApiError(null);
    setSavedToDatabase(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to use AI Project Advisor");
        return;
      }
      const projectTasks = selectedProject.tasks || [];
      const teamGuidelines = getTeamSizeGuidelines(teamSize, selectedProject.template, selectedProject.duration);
      
      const projectData = {
        project: selectedProject,
        requirements: requirements,
        availableResources: resources,
        tasks: projectTasks,
        teamSize: teamSize,
        teamSizeGuidelines: teamGuidelines
      };
      console.log('Sending project data for AI analysis:', {
        projectName: selectedProject.name,
        requirementsLength: requirements.length,
        resourcesCount: resources.length,
        tasksCount: projectTasks.length,
        teamSize: teamSize
      });
      const { data, error } = await supabase.functions.invoke('analyze-project', {
        body: projectData
      });
      if (error) {
        console.error('Error calling analyze-project function:', error);
        setApiError(error.message || 'Failed to analyze project');
        setShowNetworkAlert(true);
        return;
      }
      if (data.error) {
        console.error('API returned error:', data.error);
        setApiError(data.error);
        setShowNetworkAlert(true);
        return;
      }
      if (!data || !data.organizationStructure) {
        setShowNetworkAlert(true);
        return;
      }

      console.log('AI analysis completed successfully');

      // Enforce database rates: override any AI-provided rates with resources table values
      try {
        // Build lookup map from already-fetched resources
        const resourcesMap = new Map<string, any>();
        resources.forEach((r: any) => {
          if (r.id) resourcesMap.set(`id_${r.id}`, r);
          if (r.resource_id) resourcesMap.set(`resource_id_${r.resource_id}`, r);
          if (r.email) resourcesMap.set(`email_${r.email}`, r);
          if (r.name && r.role_type) resourcesMap.set(`name_role_${r.name}_${r.role_type}`, r);
          if (r.name) resourcesMap.set(`name_${r.name}`, r);
        });

        const getDbRateFor = (rec: any) => {
          let matched: any = null;
          if (rec?.resource_id || rec?.id) {
            const idToMatch = rec.resource_id || rec.id;
            matched = resourcesMap.get(`id_${idToMatch}`) || resourcesMap.get(`resource_id_${idToMatch}`);
          }
          if (!matched && rec?.email) matched = resourcesMap.get(`email_${rec.email}`);
          if (!matched && rec?.name && rec?.role_type) matched = resourcesMap.get(`name_role_${rec.name}_${rec.role_type}`);
          if (!matched && rec?.name) matched = resourcesMap.get(`name_${rec.name}`);
          // Preserve null or 0 from DB; never inject defaults
          return matched ? (matched.rate ?? 0) : 0;
        };

        const sanitizedData = { ...data } as any;

        if (Array.isArray(sanitizedData.recommendedResources)) {
          sanitizedData.recommendedResources = sanitizedData.recommendedResources.map((r: any) => ({
            ...r,
            rate: getDbRateFor(r)
          }));
        }

        if (Array.isArray(sanitizedData.taskAllocations)) {
          sanitizedData.taskAllocations = sanitizedData.taskAllocations.map((t: any) => ({
            ...t,
            assignedResources: Array.isArray(t.assignedResources)
              ? t.assignedResources.map((ar: any) => ({
                  ...ar,
                  rate: getDbRateFor(ar)
                }))
              : []
          }));
        }

        setRecommendation(sanitizedData);
      } catch (e) {
        console.warn('Rate sanitization skipped due to error:', e);
        setRecommendation(data);
      }

      setSavedToDatabase(true);
      toast.success("AI analysis completed and saved to database!");
    } catch (error) {
      console.error('Error analyzing project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to analyze project. Please try again.';
      setApiError(errorMessage);
      setShowNetworkAlert(true);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getTeamStatsFromRecommendation = () => {
    if (!recommendation?.organizationStructure?.nodes) {
      return { directors: 0, managers: 0, teamLeads: 0, teamMembers: 0 };
    }

    return {
      directors: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'project-director').length,
      managers: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'project-manager').length,
      teamLeads: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'team-lead').length,
      teamMembers: recommendation.organizationStructure.nodes.filter((n: any) => n.roleType === 'team-member').length
    };
  };

  return {
    requirements,
    setRequirements,
    teamSize,
    setTeamSize,
    isAnalyzing,
    recommendation,
    resources,
    activeAnalysis,
    setActiveAnalysis,
    apiError,
    savedToDatabase,
    showNetworkAlert,
    setShowNetworkAlert,
    analyzeProject,
    getTeamStatsFromRecommendation
  };
};
